'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { searchSettlements } from '@/utils/api';
import {
  advanceAddressFocus,
  assignInputRef,
} from '@/components/ui/addressSearchFocus';
import styles from './addressSearchInput.module.css';

// Full display for matching: "Padvariai k. Bartninkų, Vilkaviškio rajono"
function getFullSettlementDisplay(settlement) {
  const namePart = settlement.settlement_type
    ? `${settlement.name || ''} ${settlement.settlement_type}`.trim()
    : settlement.name || '';
  const locationPart = [settlement.eldership_name, settlement.municipality_name]
    .filter(Boolean)
    .join(', ');
  return locationPart ? `${namePart} ${locationPart}`.trim() : namePart;
}

// Extract short search term when full string returns no results (API expects "padvariai", not full address)
function getFallbackSearchTerm(term) {
  const t = term.trim();
  const firstWord = t.split(/\s+/)[0];
  return firstWord && firstWord.length >= 2 ? firstWord : null;
}

export default function AddressSearchInput({
  value = '',
  onChange,
  onSettlementSelect = null, // Optional callback with full settlement object
  placeholder = 'Įveskite miestą ar gyvenvietę',
  className = '',
  error = false,
  displayValue = null, // Optional display value (settlement name) when value is a code
  displayOnlyCityName = false, // If true, display only city name without settlement type
  displayOnlySettlementName = false, // If true, display only settlement (name+type), omit eldership/municipality
  onEnterPress = null, // Optional callback when Enter key is pressed
  nextInputRef = null, // Auto-focus next input after selection (city → street → …)
  inputRef: externalInputRef = null,
  municipalityCode = null, // Optional: filter settlements by municipality code
}) {
  const [searchTerm, setSearchTerm] = useState(displayValue || value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const resultsRef = useRef([]);
  resultsRef.current = results;

  // Format settlement display name
  const formatSettlementName = settlement => {
    if (displayOnlyCityName) {
      return settlement.name || '';
    }
    const type = settlement.settlement_type || '';
    const name = settlement.name || '';
    return type ? `${name} ${type}` : name;
  };

  // Initialize search term and selectedSettlement from displayValue/value (e.g. synced from Step 3)
  useEffect(() => {
    if (displayValue !== null && displayValue !== undefined) {
      setSearchTerm(displayValue);
    } else if (value && !displayValue) {
      setSearchTerm(value);
    }
    // When we have both value (settlement_code) and displayValue, treat as pre-selected
    if (value && displayValue) {
      setSelectedSettlement({ code: value, name: displayValue });
    }
  }, [value, displayValue]);

  // Clear pre-selection when user clears the input
  useEffect(() => {
    if (!value && !displayValue && selectedSettlement?.code) {
      setSelectedSettlement(null);
    }
  }, [value, displayValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search: try full term first, fallback to first word if no results
  const performSearch = useCallback(
    async term => {
      if (!term || term.trim().length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        let settlements = await searchSettlements(term.trim(), municipalityCode);
        if (!settlements || settlements.length === 0) {
          const fallback = getFallbackSearchTerm(term);
          if (fallback && fallback !== term.trim()) {
            settlements = await searchSettlements(
              fallback,
              municipalityCode
            );
          }
        }
        setResults(settlements || []);
        setIsOpen(true);
        setHighlightedIndex(-1);
      } catch (err) {
        console.error('[AddressSearchInput] Search error:', err);
        setResults([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [municipalityCode]
  );

  // Handle input change with debounce
  const handleInputChange = e => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!newValue || newValue.trim() === '') {
      setSelectedSettlement(null);
      setResults([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      onChange('');
      return;
    }

    // User is typing - clear selection so we can search and re-select
    setSelectedSettlement(null);

    debounceTimerRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  // On blur: only accept an exact typed match (never force-pick first result).
  const confirmTypedValueIfMatch = useCallback(() => {
    const trimmed = searchTerm?.trim();
    if (!trimmed || trimmed.length < 2 || selectedSettlement || isLoading) return;

    const currentResults = resultsRef.current;
    if (!currentResults.length) return;

    const normalizedTerm = trimmed.toLowerCase();
    const match = currentResults.find(s => {
      const full = getFullSettlementDisplay(s).trim().toLowerCase();
      const short = formatSettlementName(s).trim().toLowerCase();
      const nameOnly = (s.name || '').trim().toLowerCase();
      return (
        full === normalizedTerm ||
        short === normalizedTerm ||
        nameOnly === normalizedTerm
      );
    });
    if (!match) return;

    setSelectedSettlement(match);
    setSearchTerm(
      displayOnlySettlementName
        ? formatSettlementName(match)
        : match.eldership_name || match.municipality_name
          ? getFullSettlementDisplay(match)
          : formatSettlementName(match)
    );
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    onChange(match.code);
    if (onSettlementSelect) onSettlementSelect(match);
  }, [
    searchTerm,
    selectedSettlement,
    isLoading,
    onChange,
    onSettlementSelect,
    displayOnlySettlementName,
  ]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }
      confirmTypedValueIfMatch();
      setIsOpen(false);
      if (selectedSettlement) {
        const display = displayOnlySettlementName
          ? formatSettlementName(selectedSettlement)
          : selectedSettlement.eldership_name ||
              selectedSettlement.municipality_name
            ? getFullSettlementDisplay(selectedSettlement)
            : selectedSettlement.name ||
              formatSettlementName(selectedSettlement);
        setSearchTerm(display);
      } else if (!value && !searchTerm) {
        setSearchTerm('');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [
    selectedSettlement,
    value,
    searchTerm,
    confirmTypedValueIfMatch,
    displayOnlySettlementName,
  ]);

  // Handle input focus
  const handleFocus = () => {
    if (selectedSettlement) return; // Already selected, no need to search
    if (results.length > 0) {
      setIsOpen(true);
    } else if (searchTerm && searchTerm.trim().length >= 2) {
      performSearch(searchTerm);
    }
  };

  // Handle settlement selection
  const handleSelectSettlement = settlement => {
    setSelectedSettlement(settlement);
    const display = displayOnlySettlementName
      ? formatSettlementName(settlement)
      : settlement.eldership_name || settlement.municipality_name
        ? getFullSettlementDisplay(settlement)
        : formatSettlementName(settlement);
    setSearchTerm(display);
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    onChange(settlement.code);
    if (onSettlementSelect) {
      onSettlementSelect(settlement);
    }
    advanceAddressFocus({ nextInputRef, onEnterPress });
  };

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        className={`${styles.inputWrapper} ${error ? styles.inputError : ''} ${isLoading ? styles.loading : ''} ${className}`}
      >
        <input
          ref={node => assignInputRef(inputRef, externalInputRef, node)}
          type='text'
          className={styles.input}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={() => setTimeout(confirmTypedValueIfMatch, 0)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (isOpen && results.length > 0) {
                const indexToSelect =
                  highlightedIndex >= 0 ? highlightedIndex : 0;
                const settlementToSelect = results[indexToSelect];
                if (settlementToSelect) {
                  handleSelectSettlement(settlementToSelect);
                }
              } else if (!isOpen) {
                advanceAddressFocus({ nextInputRef, onEnterPress });
              }
            } else if (e.key === 'ArrowDown' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev =>
                prev < results.length - 1 ? prev + 1 : prev
              );
            }
            // Arrow up - navigate up in results
            else if (e.key === 'ArrowUp' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
            }
            // Escape - close dropdown
            else if (e.key === 'Escape' && isOpen) {
              e.preventDefault();
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }}
          placeholder={placeholder}
          autoComplete='off'
        />
        {isLoading && (
          <div className={styles.iconWrapper}>
            <div className={styles.spinner}></div>
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul className={styles.resultsList}>
          {results.map((settlement, index) => (
            <li
              key={settlement.code || index}
              className={`${styles.resultItem} ${
                selectedSettlement?.code === settlement.code
                  ? styles.active
                  : ''
              } ${highlightedIndex === index ? styles.highlighted : ''}`}
              onMouseDown={e => {
                // Select before input blur runs and avoid blur fallback race.
                e.preventDefault();
                handleSelectSettlement(settlement);
              }}
              onClick={() => handleSelectSettlement(settlement)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className={styles.resultName}>
                {formatSettlementName(settlement)}
              </div>
              {(settlement.eldership_name || settlement.municipality_name) && (
                <div className={styles.resultMunicipality}>
                  {settlement.eldership_name && settlement.municipality_name
                    ? `${settlement.eldership_name}, ${settlement.municipality_name}`
                    : settlement.eldership_name || settlement.municipality_name}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOpen &&
        !isLoading &&
        searchTerm.trim().length >= 2 &&
        results.length === 0 && (
          <ul className={styles.resultsList}>
            <li className={styles.noResults}>
              Nerasta rezultatų &ldquo;{searchTerm}&rdquo;
            </li>
          </ul>
        )}
    </div>
  );
}
